import { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import { useLocation, useParams } from "react-router-dom";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import ThankYouOverlay from "../../components/ThankYouOverlay";
import t from "../../service/translate.service";
import { extractError } from "../../service/response.service";

const ContactDetailsUpdateHandler = ({ contractStore }) => {
  const location = useLocation();
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null); 

  useEffect(() => {
    const processCode = async () => {
      const isUndo = location.pathname.includes("undo");

      if (!code) {
        setResult({ type: "error", isUndo });
        setLoading(false);
        return;
      }

      try {
        const response = isUndo
          ? await contractStore.undoContactDetailsUpdate(code)
          : await contractStore.confirmContactDetailsUpdate(code);

        if (response?.success) {
          if (response.contractNumber) {
            const contract = contractStore.activeContracts?.find(c => c.contractNumber === response.contractNumber);
            if (contract) contractStore.selectedContract = contract;
          }
          setResult({
            type: "success",
            isUndo,
            source: response.source || "new_sr",
            contractNumber: response.contractNumber || "",
          });
        } else {
          setResult({ type: "error", isUndo });
        }
      } catch (err) {
        const errorCode = extractError(err, null) || err?.message;
        const type = errorCode?.includes("expired") ? "expired" 
          : errorCode === "link_already_used" ? "already_used" 
          : "error";
        const errorData = err?.response?.data || {};
        setResult({ 
          type, 
          isUndo,
          source: errorData.source || "new_sr",
          contractNumber: errorData.contractNumber || "",
        });
      } finally {
        setLoading(false);
      }
    };

    processCode();
  }, []);

  if (loading) return <LoadingPageOverlay />;
  if (!result) return null;

  const isSuccess = result.type === "success";

  const title = isSuccess
    ? (result.isUndo ? t.CONTACT_DETAILS_UNDO_SUCCESS_TITLE : t.CONTACT_DETAILS_CONFIRM_SUCCESS_TITLE)
    : t[`CONTACT_DETAILS_${result.type.toUpperCase()}_TITLE`];

  const description = isSuccess
    ? (result.isUndo ? t.CONTACT_DETAILS_UNDO_SUCCESS_MESSAGE : t.CONTACT_DETAILS_CONFIRM_SUCCESS_MESSAGE)
    : t[`CONTACT_DETAILS_${result.type.toUpperCase()}_MESSAGE`];

  const buttonText = isSuccess ? t.CONTACT_DETAILS_CONTINUE : t.CONTACT_DETAILS_BACK_TO_LIST;

  const getButtonLink = () => {
    if (result.source === "new_sr") return "/repairs-and-inquiries/new-request";
    return result.contractNumber ? `/contract-overview/${result.contractNumber}` : "/contracts";
  };

  const buttonLink = getButtonLink();

  return (
    <ThankYouOverlay
      title={title}
      description={description}
      buttonText={buttonText}
      buttonLink={buttonLink}
    />
  );
};

export default inject("contractStore")(observer(ContactDetailsUpdateHandler));
